import {
  CheckIcon,
  Cross1Icon,
  Cross2Icon,
  MagnifyingGlassIcon,
  ReloadIcon,
} from "@radix-ui/react-icons";
import { observer } from "mobx-react-lite";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";

import LoadingSpinner from "../LoadingSpinner";

import { useExamples } from "./examples-store";
import SelectedExamplesBanner from "./SelectedExamplesBanner";
import { Example } from "./types";

import { Button } from "@/components/ui/button";
import Checkbox from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import FileDisplay from "@/components/ui/FileDisplay";
import { useFileContent } from "@/components/ui/FileDisplay/hooks/useFileContent";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { GenericTablePagination } from "@/components/ui/table-pagination";
import { BasicTooltip as Tooltip } from "@/components/ui/Tooltip";
import { useAuth } from "@/context/auth-context";
import { useDatasetRows } from "@/queries/datasets";

interface ExampleSelectionDialogProps {
  datasetId: number;
  onAddExample: (example: Example) => void;
  onClose?: () => void;
  onSelect?: () => void;
  selectedExamples: Example[];
  singleSelect?: boolean;
  maxSelectable?: number;
  defaultOpen?: boolean;
  hideTrigger?: boolean;
}

const ITEMS_PER_PAGE = 10;
const pageSizeOptions = [10, 20, 30, 40, 50];

const TableLoadingState = () => (
  <div className="space-y-3 p-4">
    {[...Array(ITEMS_PER_PAGE)].map((_, i) => (
      <div
        // eslint-disable-next-line react/no-array-index-key
        key={i}
        className="h-10 animate-pulse rounded bg-gray-100"
        role="progressbar"
        aria-label="Loading"
      />
    ))}
  </div>
);

const EmptyTableState = () => (
  <div className="flex h-64 items-center justify-center">
    <p className="text-sm text-gray-500">No examples found</p>
  </div>
);

const highlightText = (text: string, query: string) => {
  if (!query) {
    return text;
  }
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, index) =>
    part.toLowerCase() === query.toLowerCase() ? (
      // eslint-disable-next-line react/no-array-index-key
      <span key={index} className="rounded bg-yellow-200 px-0.5">
        {part}
      </span>
    ) : (
      part
    ),
  );
};

const TableContentWrapper = ({
  isLoading,
  isSearching,
  hasData,
  children,
}: {
  isLoading: boolean;
  isSearching: boolean;
  hasData: boolean;
  children: React.ReactNode;
}) => {
  if (isLoading || isSearching) {
    return <TableLoadingState />;
  }

  if (!hasData) {
    return <EmptyTableState />;
  }

  return <>{children}</>;
};

const CellContent = ({
  value,
  searchQuery,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  searchQuery: string;
}) => {
  const stringValue = typeof value === "string" ? value : JSON.stringify(value);
  const { fileItems, isFileContent, isProcessingFile } = useFileContent(stringValue);

  if (!value || value === "") {
    return (
      <div className="max-h-[100px] overflow-x-auto overflow-y-auto break-words">
        <span className="text-gray-400">-</span>
      </div>
    );
  }

  if (isProcessingFile) {
    return (
      <div className="max-h-[100px] overflow-x-auto overflow-y-auto break-words">
        <div className="flex items-center gap-2">
          <LoadingSpinner size={4} />
        </div>
      </div>
    );
  }

  if (isFileContent && fileItems && fileItems.length > 0) {
    return (
      <div className="max-h-[100px] overflow-x-auto overflow-y-auto">
        {fileItems.length === 1 ? (
          <FileDisplay
            data={fileItems[0].data}
            detectedFile={fileItems[0].fileType}
            className="max-w-[280px]"
          />
        ) : (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,200px))] gap-2">
            {fileItems.map((item, index) => (
              <FileDisplay
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                data={item.data}
                detectedFile={item.fileType}
                className="max-w-[280px]"
                isInCollection
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  const displayValue = highlightText(stringValue, searchQuery);
  return (
    <div className="max-h-[100px] overflow-x-auto overflow-y-auto break-words">{displayValue}</div>
  );
};

const ExampleSelectionDialog = ({
  datasetId,
  onAddExample,
  onClose,
  onSelect,
  singleSelect = false,
  maxSelectable,
  defaultOpen = false,
  hideTrigger = false,
}: ExampleSelectionDialogProps) => {
  const store = useExamples();
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(ITEMS_PER_PAGE);

  const { userToken } = useAuth() || {};

  if (!userToken) {
    throw new Error("User token is required");
  }
  const {
    data: rowsData,
    isLoading: isQueryLoading,
    isFetching,
    error,
  } = useDatasetRows({
    userToken,
    dataset_id: datasetId,
    page: currentPage,
    pageSize,
    sortBy: "id",
    sortOrder: "desc",
    q: searchQuery,
  });

  if (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to fetch dataset rows:", error);
  }

  const isLoading = isQueryLoading || isFetching;
  const totalPages = rowsData?.pages ?? 0;
  const hasNext = currentPage < totalPages;

  const resultData = useMemo(
    () =>
      rowsData?.dataset_rows?.map(({ variables, id }) => ({
        value: variables,
        id,
      })),
    [rowsData],
  );

  useEffect(() => {
    const firstRowValue = resultData?.[0]?.value;

    if (!firstRowValue) {
      return;
    }

    const allColumns = Object.keys(firstRowValue);

    if (store.visibleColumns.length === 0) {
      store.setVisibleColumns(allColumns);
      return;
    }

    const totalCurrentColumns = store.visibleColumns.length + store.removedColumns.length;
    if (totalCurrentColumns !== allColumns.length) {
      const newRemovedColumns = allColumns.filter(column => !store.visibleColumns.includes(column));
      store.setRemovedColumns(newRemovedColumns);
    }
  }, [resultData, store, store.visibleColumns.length]);

  useEffect(() => {
    if (isOpen && resultData && resultData.length > 0 && store.visibleColumns.length === 0) {
      // If we're opening the dialog and have data but no visible columns,
      // initialize the columns from the first row
      const firstRowValue = resultData[0]?.value;
      if (firstRowValue) {
        store.setVisibleColumns(Object.keys(firstRowValue));
      }
    }
  }, [isOpen, resultData, store]);

  useEffect(() => {
    setIsSearching(!!searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (!isQueryLoading && rowsData?.dataset_rows) {
      setIsSearching(false);
    }
  }, [isQueryLoading, rowsData?.dataset_rows]);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  }, []);

  const toggleColumn = useCallback(
    (columnName: string) => {
      store.toggleColumn(columnName);
    },
    [store],
  );

  const isExampleSelected = useCallback(
    (example: Example) => store.selectedExamples.some(selected => selected.id === example.id),
    [store.selectedExamples],
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const handleSelect = useCallback(() => {
    onSelect?.();
    setIsOpen(false);
    onClose?.();
  }, [onSelect, onClose]);

  const isAtMaxSelection =
    maxSelectable !== undefined && store.selectedExamples.length >= maxSelectable;

  const handleAddExample = useCallback(
    (example: Example) => {
      const isSelected = isExampleSelected(example);

      // If trying to add (not deselect) and at max, don't allow
      if (
        !isSelected &&
        maxSelectable !== undefined &&
        store.selectedExamples.length >= maxSelectable
      ) {
        return;
      }

      const value = store.visibleColumns.reduce(
        (acc, key) => {
          acc[key] = example.value[key];
          return acc;
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {} as Record<string, any>,
      );

      if (singleSelect && !isSelected) {
        // In single select mode, clear previous selections before adding the new one
        store.resetExamples();
      }

      onAddExample({ id: example.id, value });
    },
    [store, singleSelect, isExampleSelected, onAddExample, maxSelectable],
  );

  const handleSelectAll = useCallback(() => {
    if (singleSelect) {
      // In single select mode, don't allow selecting all
      return;
    }

    resultData?.forEach((example: Example) => {
      // Check if we've reached the max before adding each example
      if (maxSelectable !== undefined && store.selectedExamples.length >= maxSelectable) {
        return;
      }
      if (!isExampleSelected(example)) {
        handleAddExample(example);
      }
    });
  }, [
    resultData,
    handleAddExample,
    isExampleSelected,
    singleSelect,
    maxSelectable,
    store.selectedExamples.length,
  ]);

  const handleDeselectAll = useCallback(() => {
    store.selectedExamples.forEach(example => {
      onAddExample(example);
    });
  }, [onAddExample, store.selectedExamples]);

  const handleSetPageSize = useCallback((newSize: number, page = 1) => {
    setPageSize(newSize);
    setCurrentPage(page);
  }, []);

  const allSelected = (resultData?.length || 0) > 0 && resultData?.every(isExampleSelected);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <SelectedExamplesBanner
            onClick={() => setIsOpen(true)}
            selectedCount={store.selectedExamples.length}
            singleSelect={singleSelect}
          />
        </DialogTrigger>
      )}
      <DialogContent className="max-h-[90vh] w-full overflow-hidden sm:max-w-[900px]">
        <div className="relative mt-4 w-full space-y-4 overflow-hidden">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">
              {singleSelect ? "Select an Input" : "Add New Examples"}
            </h2>
            <div className="flex items-center gap-3">
              {store.selectedExamples.length > 0 && !singleSelect && (
                <div
                  className="animate-fadeIn rounded-full bg-blue-100 px-3 py-1 text-sm
                    font-medium text-blue-700"
                >
                  {store.selectedExamples.length}
                  {maxSelectable !== undefined ? ` / ${maxSelectable}` : ""} selected
                </div>
              )}
              {isAtMaxSelection && (
                <div
                  className="animate-fadeIn rounded-full bg-amber-100 px-3 py-1 text-sm
                    font-medium text-amber-700"
                >
                  Maximum reached
                </div>
              )}
              {!singleSelect && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSelectAll}
                    disabled={isLoading || !resultData?.length || allSelected || isAtMaxSelection}
                    className="flex h-8 items-center gap-2 whitespace-nowrap px-3"
                  >
                    <CheckIcon className="h-4 w-4" />
                    Select All on Page
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDeselectAll}
                    disabled={isLoading || !store.selectedExamples.length}
                    className="flex h-8 items-center gap-2 whitespace-nowrap px-3"
                  >
                    <Cross1Icon className="h-4 w-4" />
                    Clear All Selections
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder={`${singleSelect ? "Search inputs" : "Search examples"}`}
                value={searchQuery}
                onChange={handleSearchChange}
                className="h-10 pl-10"
                aria-label="Search examples"
              />
            </div>
            {store.removedColumns.length > 0 && (
              <Tooltip content="Restore last removed column">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    toggleColumn(store.removedColumns[store.removedColumns.length - 1])
                  }
                  disabled={isLoading}
                  className="h-10 whitespace-nowrap hover:bg-gray-50"
                >
                  <ReloadIcon className="mr-2 h-4 w-4" />
                  Restore Column
                </Button>
              </Tooltip>
            )}
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
            <TableContentWrapper
              isLoading={isLoading}
              isSearching={isSearching}
              hasData={Boolean(resultData?.length)}
            >
              <div className="max-h-[500px] max-w-[900px] overflow-auto">
                <Table className="w-full">
                  <TableHeader className="sticky top-0 z-10 border-b border-gray-300 bg-gray-50">
                    <TableRow>
                      <TableHead className="sticky left-0 z-20 w-16 bg-gray-50">
                        <span className="sr-only">Selection column</span>
                      </TableHead>
                      {store.visibleColumns.map(key => (
                        <TableHead
                          key={key}
                          className="group relative min-w-[150px] bg-gray-50 pr-10 font-semibold"
                        >
                          <div className="overflow-x-auto">{key}</div>
                          {store.visibleColumns.length > 1 && !singleSelect && (
                            <Tooltip content={`Remove ${key} column`}>
                              <Button
                                size="tinyIcon"
                                variant="ghost"
                                className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0
                                  transition-opacity group-hover:opacity-100"
                                onClick={() => toggleColumn(key)}
                              >
                                <Cross2Icon className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resultData?.map((example: Example) => {
                      const selected = isExampleSelected(example);
                      const isDisabled = !selected && isAtMaxSelection;

                      const getRowClassName = () => {
                        if (selected) {
                          return "bg-blue-50 hover:bg-blue-100";
                        }
                        if (isDisabled) {
                          return "cursor-not-allowed opacity-50";
                        }
                        return "hover:bg-gray-50";
                      };

                      return (
                        <TableRow
                          key={example.id}
                          className={`group transition-colors ${getRowClassName()}`}
                          onClick={() => !isDisabled && handleAddExample(example)}
                          style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                        >
                          <TableCell
                            className="sticky left-4 z-10"
                            onClick={e => e.stopPropagation()}
                          >
                            {isDisabled ? (
                              <Tooltip content={`Maximum of ${maxSelectable} rows can be selected`}>
                                <div>
                                  <Checkbox
                                    checked={selected}
                                    disabled
                                    className="h-5 w-5 cursor-not-allowed opacity-50
                                      transition-all duration-200"
                                    aria-label="Selection disabled - maximum reached"
                                  />
                                </div>
                              </Tooltip>
                            ) : (
                              <Checkbox
                                checked={selected}
                                onChange={() => handleAddExample(example)}
                                className="h-5 w-5 transition-all duration-200"
                                aria-label={
                                  singleSelect ? "Select this example" : "Toggle selection"
                                }
                                {...(singleSelect ? { type: "radio" } : {})}
                              />
                            )}
                          </TableCell>
                          {store.visibleColumns.map(key => (
                            <TableCell key={key} className="min-w-[150px] max-w-[300px] py-2">
                              <CellContent value={example.value[key]} searchQuery={searchQuery} />
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </TableContentWrapper>
          </div>

          <div className="flex items-center justify-between pt-2">
            <GenericTablePagination
              pages={totalPages}
              hasNext={hasNext}
              hasPrev={currentPage > 1}
              page={currentPage}
              pageSize={pageSize}
              pageSizeOptions={pageSizeOptions}
              setPageIndex={setCurrentPage}
              setPageSize={handleSetPageSize}
            />

            <div className="ml-auto flex gap-2">
              <Button
                size="default"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
                className="hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button
                size="default"
                onClick={handleSelect}
                disabled={isLoading || store.selectedExamples.length === 0}
                className="hover:bg-blue-600"
              >
                {singleSelect ? "Select" : "Done"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default observer(ExampleSelectionDialog);
